import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Seo, { publicUrl } from "@/components/Seo";
import { Leaf, MapPin, Banknote, Briefcase, ArrowLeft, CheckCircle2 } from "lucide-react";

/** Map our internal contract types to schema.org `employmentType`. */
function schemaEmploymentType(j) {
  const ct = (j.contract_type || "").toLowerCase();
  const et = (j.employment_type || "").toLowerCase();
  if (ct.includes("permanent") && et.includes("part")) return "PART_TIME";
  if (ct.includes("permanent")) return "FULL_TIME";
  if (ct.includes("fixed")) return "TEMPORARY";
  if (ct.includes("bank") || ct.includes("relief") || ct.includes("zero")) return "CONTRACTOR";
  if (et.includes("part")) return "PART_TIME";
  return "FULL_TIME";
}

/** Build the JobPosting JSON-LD per https://schema.org/JobPosting and the
 * Google Jobs requirements. baseSalary omitted entirely when no salary is
 * set (Google rejects empty MonetaryAmount). */
function buildJobPostingLd(j) {
  const ld = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": j.title,
    "description": `<p>${(j.description || "").replace(/</g, "&lt;")}</p>` +
      (j.requirements?.length
        ? `<h3>What you'll need</h3><ul>${j.requirements.map((r) => `<li>${r}</li>`).join("")}</ul>`
        : ""),
    "identifier": { "@type": "PropertyValue", "name": "Rabah Concept Recruitment", "value": j.id },
    "datePosted": (j.created_at || "").slice(0, 10),
    "employmentType": schemaEmploymentType(j),
    "hiringOrganization": {
      "@type": "Organization",
      "name": "Rabah Concept Recruitment",
      "sameAs": publicUrl("/"),
      "logo": publicUrl("/rabah-logo.png"),
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": j.location || "United Kingdom",
        "addressCountry": "GB",
      },
    },
    "applicantLocationRequirements": { "@type": "Country", "name": "GB" },
    "directApply": true,
  };
  if (j.contract_end_date) ld.validThrough = new Date(j.contract_end_date).toISOString();
  // Salary parsing — show only when we can derive numeric min/max.
  const sal = (j.salary || "").replace(/£|,/g, "");
  const numbers = sal.match(/\d+(?:\.\d+)?/g);
  if (numbers && numbers.length >= 1) {
    const min = parseFloat(numbers[0]);
    const max = parseFloat(numbers[1] || numbers[0]);
    const unit = /hour|hr|\/h/i.test(j.salary || "") ? "HOUR" : "YEAR";
    ld.baseSalary = {
      "@type": "MonetaryAmount",
      "currency": "GBP",
      "value": { "@type": "QuantitativeValue", "minValue": min, "maxValue": max, "unitText": unit },
    };
  }
  return ld;
}

export default function PortalJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [j, setJ] = useState(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    api.get(`/portal/jobs/${id}`)
      .then(({ data }) => setJ(data))
      .catch((e) => {
        if (e?.response?.status === 410) {
          // REC-10 — closed/archived: 301-style soft redirect to /jobs
          setGone(true);
          setTimeout(() => nav("/jobs", { replace: true }), 1500);
        } else if (e?.response?.status === 404) {
          nav("/jobs", { replace: true });
        }
      });
  }, [id, nav]);

  if (gone) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center" style={{ background: "var(--cc-bg)" }}>
        <Seo title="Vacancy closed | Rabah Concept Recruitment" noindex description="This vacancy is no longer accepting applications." canonical={publicUrl("/jobs")} />
        <div>
          <h1 className="font-display text-3xl font-bold mb-3">This vacancy is closed</h1>
          <p className="text-[var(--cc-text-soft)] mb-4">Redirecting you to the current vacancies…</p>
          <Link className="btn-primary" to="/jobs">View open roles</Link>
        </div>
      </div>
    );
  }
  if (!j) return <div className="p-8 text-[var(--cc-text-soft)]">Loading…</div>;

  const canonicalSlug = j.slug || j.id;
  const firstSentence = (j.description || "").split(/[.!?]\s/)[0].slice(0, 110);
  const metaDesc = `${j.title} role at Rabah Concept Recruitment in ${j.location || "the UK"}. ${j.contract_type || j.employment_type || ""}${j.salary ? `, ${j.salary}` : ""}. ${firstSentence}. Apply now.`.replace(/\s+/g, " ").slice(0, 160);
  const pageTitle = `${j.title} — ${j.location || "UK"} | Rabah Concept Recruitment`;

  return (
    <div className="min-h-screen" style={{ background: "var(--cc-bg)" }}>
      <Seo
        title={pageTitle}
        description={metaDesc}
        canonical={publicUrl(`/jobs/${canonicalSlug}`)}
        type="article"
        jsonLd={buildJobPostingLd(j)}
      />
      <header className="cc-topbar px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-4 h-4 text-white" />
          </div>
          <div className="font-display font-bold text-lg">CareConnect</div>
        </Link>
        <Link to="/jobs" className="btn-ghost"><ArrowLeft className="w-4 h-4 inline mr-1" />All jobs</Link>
      </header>

      <article className="max-w-3xl mx-auto px-8 py-16 fade-up pb-32">
        <div className="label-eyebrow mb-3">{j.department}</div>
        <h1 className="font-display text-5xl font-bold mb-5">{j.title}</h1>
        <div className="flex flex-wrap gap-4 text-sm text-[var(--cc-text-soft)] mb-10">
          <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{j.location}</span>
          <span className="flex items-center gap-1.5"><Banknote className="w-4 h-4" />{j.salary}</span>
          <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" />{j.contract_type || j.employment_type}</span>
          {j.hours_per_week && <span>· {j.hours_per_week} hrs/week</span>}
          {j.service_type && <span className="pill pill-info !text-[10px]">{j.service_type}</span>}
        </div>

        <section className="cc-card p-8 mb-6">
          <h2 className="font-display text-2xl font-bold mb-3">About the role</h2>
          <p className="text-base leading-relaxed text-[var(--cc-text-soft)]">{j.description}</p>
        </section>

        {j.requirements?.length > 0 && (
          <section className="cc-card p-8 mb-8">
            <h2 className="font-display text-2xl font-bold mb-4">What we're looking for</h2>
            <ul className="space-y-2">
              {j.requirements.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="w-4 h-4 mt-1 text-[var(--cc-primary)] shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="flex gap-3">
          <Link to={`/apply/${j.id}`} className="btn-primary text-base py-3" data-testid="apply-now-button">Apply now</Link>
          <Link to="/jobs" className="btn-secondary text-base py-3">Browse more roles</Link>
        </div>
      </article>

      {/* REC-29 — Sticky mobile Apply button, always reachable */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 p-3 bg-[var(--cc-bg)] border-t border-[var(--cc-border)]"
           style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <Link to={`/apply/${j.id}`}
              className="btn-primary w-full justify-center text-base py-3"
              style={{ minHeight: 44 }}
              data-testid="apply-now-mobile-button">
          Apply now
        </Link>
      </div>
    </div>
  );
}
